import axiosInstance from '../../utils/axiosInstance';
import {
  DeviceLimitReachedError,
  isDeviceLimitReachedPayload,
} from '../../utils/apiErrors';

/**
 * Mobile Devices API client.
 *
 * Endpointi (backend preuzima EmployeeId/InstitutionId iz JWT-a):
 *   POST   /api/mobile-devices/register
 *   GET    /api/mobile-devices
 *   DELETE /api/mobile-devices/{deviceId}
 *
 * Ovde se NE šalju employeeId ni institutionId.
 */

/**
 * Unwrap the ApiResponse envelope; fall back to the raw body.
 * @param {Object} response - Axios response
 * @returns {*} Payload
 */
const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    return body.data;
  }
  return body;
};

const isSupportedPlatform = (platform) => platform === 'ios' || platform === 'android';

const mobileDevicesApi = {
  /**
   * Registruj (ili osveži) uređaj za push notifikacije.
   * Backend po `deviceId` update-uje postojeći EmployeeDevice, pa se isti
   * deviceId šalje i kada se promeni push token.
   *
   * @param {Object} payload
   * @param {string} payload.deviceId
   * @param {'ios'|'android'} payload.platform
   * @param {string} [payload.deviceName]
   * @param {string} payload.pushToken
   * @returns {Promise<Object|null>} Registrovani uređaj (ako ga backend vraća)
   */
  async registerDevice({ deviceId, platform, deviceName, pushToken } = {}) {
    if (!deviceId) {
      throw new Error('DeviceId je obavezan za registraciju uređaja.');
    }
    if (!isSupportedPlatform(platform)) {
      throw new Error('Platforma uređaja mora biti ios ili android.');
    }
    if (!pushToken) {
      throw new Error('Push token je obavezan za registraciju uređaja.');
    }

    try {
      const response = await axiosInstance.post('/api/mobile-devices/register', {
        deviceId,
        platform,
        deviceName: deviceName || (platform === 'ios' ? 'iOS device' : 'Android device'),
        pushToken,
      });
      return unwrap(response);
    } catch (error) {
      if (isDeviceLimitReachedPayload(error.response?.status, error.response?.data)) {
        throw new DeviceLimitReachedError(undefined, { cause: error });
      }
      throw error;
    }
  },

  /**
   * Lista aktivnih uređaja prijavljenog zaposlenog.
   * Pripremljeno za budući ekran „Aktivni uređaji".
   * @returns {Promise<Array>} Lista uređaja
   */
  async getDevices() {
    const response = await axiosInstance.get('/api/mobile-devices');
    const data = unwrap(response);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Ukloni/revoke-uj uređaj. Ovo je poseban security flow i ne poziva se na
   * običan logout.
   * @param {string} deviceId
   * @returns {Promise<void>}
   */
  async removeDevice(deviceId) {
    if (!deviceId) {
      throw new Error('DeviceId je obavezan za uklanjanje uređaja.');
    }
    await axiosInstance.delete(`/api/mobile-devices/${encodeURIComponent(deviceId)}`);
  },
};

export default mobileDevicesApi;
